import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckIcon, SparklesIcon, RocketIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { ContactFormPopup } from './ContactFormPopup';
export function SmsServices() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const {
    t
  } = useLanguage();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  return <>
      <section id="services" ref={ref} className="relative w-full overflow-hidden px-4 py-16 sm:py-24" style={{
      backgroundColor: 'var(--bg-primary)'
    }} data-id="element-341" data-mp="32n1yopdqrw2">
        <div className="relative z-10 mx-auto max-w-7xl" data-id="element-342" data-mp="d1m922uhyfgy">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} className="mb-12 sm:mb-16 text-center" data-id="element-343" data-mp="cyzw1nou77rc">
            <h2 className="mb-4 text-3xl font-bold md:text-4xl lg:text-5xl" style={{
            color: 'var(--text-primary)'
          }} data-id="element-344" data-mp="17sb4l7yvboda">
              {t('sms.pricing.title')}{' '}
              <span style={{
              color: 'var(--accent)'
            }} data-id="element-345" data-mp="xv6nhmjvqj3c">
                {t('sms.pricing.titleHighlight')}
              </span>
            </h2>
            <p className="mx-auto max-w-2xl text-base sm:text-lg" style={{
            color: 'var(--text-secondary)'
          }} data-id="element-346" data-mp="p2l6obzt6mh5">
              {t('sms.pricing.subtitle')}
            </p>
          </motion.div>

          {/* Pricing Cards */}
          <div className="grid gap-6 lg:grid-cols-2 max-w-5xl mx-auto" data-id="element-347" data-mp="a5cddcqy38ms">
            {/* Free Demo Preview */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.1
          }} data-id="element-348" data-mp="fvmokvybn4z">
              <div className="h-full rounded-3xl border p-8 flex flex-col" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-secondary)'
            }} data-id="element-349" data-mp="o6rma46nfmms">
                <div className="flex items-center gap-3 mb-6" data-id="element-350" data-mp="efeufckuowgr">
                  <div className="p-3 rounded-xl" style={{
                  backgroundColor: 'var(--bg-tertiary)'
                }} data-id="element-351" data-mp="1260qs6lhssax">
                    <SparklesIcon className="w-6 h-6" style={{
                    color: 'var(--accent)'
                  }} data-id="element-352" data-mp="2rmwbq31w1a36" />
                  </div>
                  <h3 className="text-xl font-bold" style={{
                  color: 'var(--text-primary)'
                }} data-id="element-353" data-mp="31c665pk7f4ug">
                    {t('pricing.demo.title')}
                  </h3>
                </div>

                <div className="mb-6" data-id="element-354" data-mp="23qs93iuoo61a">
                  <div className="inline-block px-4 py-1 rounded-full text-sm font-bold mb-2" style={{
                  backgroundColor: 'var(--accent)',
                  color: '#fff'
                }} data-id="element-355" data-mp="23rtofziqfeot">
                    {t('pricing.demo.label')}
                  </div>
                  <p className="text-sm sm:text-base font-medium mt-2" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-356" data-mp="3p3v01acoc1di">
                    {t('pricing.demo.desc')}
                  </p>
                </div>

                <ul className="space-y-4 mb-8 flex-1" data-id="element-357" data-mp="27ll70qenin4m">
                  {[1, 2, 3].map(i => <li key={i} className="flex items-start gap-3" data-id="element-358" data-mp="2cmsespgsnmba">
                      <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center" data-id="element-359" data-mp="2hl2ar2aceqxy">
                        <CheckIcon className="w-3 h-3 text-green-500" data-id="element-360" data-mp="2mivjo3315z1x" />
                      </div>
                      <span style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-361" data-mp="2rbomumiuozbw">
                        {t(`pricing.demo.b${i}`)}
                      </span>
                    </li>)}
                </ul>

                <button onClick={() => setIsPopupOpen(true)} className="block w-full rounded-xl py-4 text-center text-sm sm:text-base font-semibold transition-all hover:scale-[1.02]" style={{
                backgroundColor: 'var(--accent)',
                color: '#FFFFFF'
              }} data-id="element-362" data-mp="35awnzkykqhiq">
                  {t('pricing.demo.cta')}
                </button>
              </div>
            </motion.div>

            {/* Website Subscription */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-363" data-mp="219qlkgcnehr">
              <div className="h-full rounded-3xl border p-8 flex flex-col relative overflow-hidden" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-secondary)'
            }} data-id="element-364" data-mp="fyokp2hjsag2">
                <div className="flex items-center gap-3 mb-6 relative z-10" data-id="element-365" data-mp="20h8t2gg9k3me">
                  <div className="p-3 rounded-xl" style={{
                  backgroundColor: 'var(--bg-tertiary)'
                }} data-id="element-366" data-mp="20f6501yo8kkb">
                    <RocketIcon className="w-6 h-6" style={{
                    color: 'var(--accent)'
                  }} data-id="element-367" data-mp="1rix6qqmfjrzr" />
                  </div>
                  <h3 className="text-xl font-bold" style={{
                  color: 'var(--text-primary)'
                }} data-id="element-368" data-mp="1mh7rszw36u6e">
                    {t('sms.pricing.launch.title')}
                  </h3>
                </div>

                <div className="mb-6 relative z-10" data-id="element-369" data-mp="1585biiydv4jf">
                  <div className="flex items-baseline gap-2 flex-wrap" data-id="element-370" data-mp="158oqbqsqlct8">
                    <span className="text-2xl font-bold line-through opacity-50" style={{
                    color: 'var(--text-muted)'
                  }} data-id="element-371" data-mp="109u0i4wcqnzm">
                      €25
                    </span>
                    <span className="text-4xl font-bold" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-372" data-mp="1ny3q7hworvw1">
                      {t('sms.pricing.launch.price')}
                    </span>
                    <span className="text-sm" style={{
                    color: 'var(--text-muted)'
                  }} data-id="element-373" data-mp="1sxzm0tv8ch0i">
                      {t('sms.pricing.launch.period')}
                    </span>
                  </div>
                  <p className="text-sm sm:text-base font-medium mt-2" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-374" data-mp="38wgruior0fwh">
                    {t('sms.pricing.launch.desc')}
                  </p>
                </div>

                <ul className="space-y-4 mb-8 flex-1 relative z-10" data-id="element-375" data-mp="3nl36sjagr5n6">
                  {[1, 2, 3, 4].map(i => <li key={i} className="flex items-start gap-3" data-id="element-376" data-mp="2pycu0keba39z">
                      <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center" data-id="element-377" data-mp="2uvmfjoykxusj">
                        <CheckIcon className="w-3 h-3 text-green-500" data-id="element-378" data-mp="2zufvrp593zwe" />
                      </div>
                      <span style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-379" data-mp="34v3lm7hquap1">
                        {t(`pricing.launch.b${i}`)}
                      </span>
                    </li>)}
                </ul>

                <button onClick={() => setIsPopupOpen(true)} className="block w-full rounded-xl py-4 text-center text-sm sm:text-base font-semibold transition-all hover:scale-[1.02] relative z-10" style={{
                backgroundColor: 'var(--accent)',
                color: '#FFFFFF'
              }} data-id="element-380" data-mp="24lc9xfgc8enm">
                  {t('pricing.launch.cta')}
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <ContactFormPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} data-id="element-381" data-mp="1bx85ex9i67mi" />
    </>;
}